import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Invoice, PortalDashboard, Shipment } from '../api/types'
import {
  DueChip,
  Empty,
  ErrorBanner,
  Kpi,
  Loading,
  Panel,
  StatusChip,
  dateTime,
  inr,
} from '../components/ui'

/**
 * Still moving.
 *
 * A failed delivery is not "in flight" — it has stopped, and telling a customer
 * their consignment is on its way when the driver could not deliver it is worse
 * than saying nothing. Failures are counted separately and said out loud.
 */
function isMoving(shipment: Shipment): boolean {
  return !['delivered', 'closed', 'cancelled', 'failed'].includes(shipment.status)
}

function isFailed(shipment: Shipment): boolean {
  return ['failed', 'cancelled'].includes(shipment.status)
}

export default function DashboardPage() {
  const dashboardQuery = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: () => api.get<PortalDashboard>('/portal/dashboard').then((r) => r.data),
  })

  const shipmentsQuery = useQuery({
    queryKey: ['portal-shipments'],
    queryFn: () => api.get<Shipment[]>('/portal/shipments').then((r) => r.data),
  })

  const invoicesQuery = useQuery({
    queryKey: ['portal-invoices'],
    queryFn: () => api.get<Invoice[]>('/portal/invoices').then((r) => r.data),
  })

  const stats = dashboardQuery.data
  const shipments = shipmentsQuery.data ?? []
  const invoices = invoicesQuery.data ?? []

  const inFlight = shipments.filter(isMoving)
  const failed = shipments.filter(isFailed)
  const owing = invoices.filter((invoice) => Number(invoice.balance) > 0)

  const claimsOpen = Object.entries(stats?.claims ?? {})
    .filter(([status]) => !['settled', 'rejected', 'closed'].includes(status))
    .reduce((sum, [, count]) => sum + count, 0)

  return (
    <div className="stack">
      <div className="page-head">
        <div className="page-title">Overview</div>
        <div className="page-sub">Your consignments, invoices and claims with this transporter</div>
      </div>

      {dashboardQuery.error && <ErrorBanner error={dashboardQuery.error} />}

      {failed.length > 0 && (
        <div className="banner banner-warn" style={{ marginBottom: 0 }}>
          {failed.length === 1
            ? `Consignment ${failed[0]?.shipment_number} could not be delivered.`
            : `${failed.length} consignments could not be delivered.`}{' '}
          The transporter has been notified. You can raise a claim from the Claims tab.
        </div>
      )}

      {dashboardQuery.isLoading ? (
        <Loading rows={2} />
      ) : stats ? (
        <div className="kpi-grid">
          <Kpi
            value={inFlight.length}
            label="Consignments in flight"
            foot={`${stats.shipments.total} in total`}
          />
          <Kpi
            value={stats.shipments.delivered}
            label="Delivered"
            tone={stats.shipments.delivered > 0 ? 'good' : undefined}
          />
          <Kpi
            value={stats.shipments.late}
            label="Delivered late"
            foot={stats.shipments.late > 0 ? 'against the agreed transit time' : 'all on time'}
            tone={stats.shipments.late > 0 ? 'warn' : 'good'}
          />
          <Kpi
            value={inr(stats.invoices.outstanding)}
            label="Outstanding"
            foot={`${stats.invoices.open_count} open invoice(s)`}
            tone={Number(stats.invoices.outstanding) > 0 ? 'warn' : 'good'}
          />
          <Kpi
            value={claimsOpen}
            label="Claims open"
            foot={claimsOpen > 0 ? 'awaiting a decision' : 'nothing outstanding'}
            tone={claimsOpen > 0 ? 'warn' : undefined}
          />
        </div>
      ) : null}

      <div className="two-col">
        <Panel
          title={`In flight (${inFlight.length})`}
          action={
            <Link className="btn btn-sm" to="/shipments">
              All shipments
            </Link>
          }
          bodyless
        >
          {shipmentsQuery.isLoading ? (
            <Loading rows={3} />
          ) : shipmentsQuery.error ? (
            <div style={{ padding: 16 }}>
              <ErrorBanner error={shipmentsQuery.error} />
            </div>
          ) : inFlight.length === 0 ? (
            <Empty
              title="Nothing on the road right now"
              hint="Consignments appear here from the moment they are booked."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Consignment</th>
                  <th>Lane</th>
                  <th>Status</th>
                  <th>Promised</th>
                </tr>
              </thead>
              <tbody>
                {inFlight.slice(0, 8).map((shipment) => (
                  <tr key={shipment.id}>
                    <td data-label="Consignment" className="mono">
                      {shipment.shipment_number}
                    </td>
                    <td data-label="Lane">
                      {shipment.origin_name} → {shipment.destination_name}
                    </td>
                    <td data-label="Status">
                      <StatusChip status={shipment.status} />
                    </td>
                    <td data-label="Promised">{dateTime(shipment.promised_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel
          title={`Awaiting payment (${owing.length})`}
          action={
            <Link className="btn btn-sm" to="/invoices">
              All invoices
            </Link>
          }
          bodyless
        >
          {invoicesQuery.isLoading ? (
            <Loading rows={3} />
          ) : invoicesQuery.error ? (
            <div style={{ padding: 16 }}>
              <ErrorBanner error={invoicesQuery.error} />
            </div>
          ) : owing.length === 0 ? (
            <Empty title="Nothing outstanding" hint="Every invoice raised to you is settled." />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th className="num">Balance</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {owing.slice(0, 8).map((invoice) => (
                  <tr key={invoice.id}>
                    <td data-label="Invoice" className="mono">
                      {invoice.invoice_number}
                    </td>
                    <td data-label="Balance" className="num">
                      {inr(invoice.balance)}
                    </td>
                    <td data-label="Due">
                      <DueChip date={invoice.due_date} balance={invoice.balance} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </div>
  )
}
